/**
 * ThemeToggle — selector light / dark / system (radio group completo).
 * Se usa en ProfilePage.
 *
 * QuickThemeToggle — botón icon-only (Sun/Moon) para el header global.
 * Cicla solo entre light/dark (para "system" usar ThemeToggle en /profile).
 */
import { Sun, Moon, Monitor } from 'lucide-react'
import { useThemeStore, type ThemeMode } from '@/store/themeStore'

interface Option {
  value: ThemeMode
  label: string
  icon: typeof Sun
}

const OPTIONS: Option[] = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Oscuro', icon: Moon },
  { value: 'system', label: 'Sistema', icon: Monitor },
]

export function QuickThemeToggle() {
  const { resolvedMode, setMode } = useThemeStore()
  const isDark = resolvedMode === 'dark'

  return (
    <button
      type="button"
      onClick={() => setMode(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      className="p-2 rounded-lg text-content-muted hover:text-content hover:bg-soft transition-colors"
    >
      {isDark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  )
}

export function ThemeToggle() {
  const { mode, setMode } = useThemeStore()

  return (
    <div
      role="radiogroup"
      aria-label="Tema de la interfaz"
      className="inline-flex items-center gap-1 p-1 rounded-lg bg-glass-3 border border-divider"
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const isActive = mode === value
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => setMode(value)}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
              transition-colors outline-none cursor-pointer
              focus-visible:ring-2 focus-visible:ring-focus-ring
              ${isActive
                ? 'bg-surface text-content shadow-sm'
                : 'text-content-muted hover:text-content hover:bg-soft'
              }
            `}
          >
            <Icon size={14} aria-hidden="true" />
            <span>{label}</span>
          </button>
        )
      })}
    </div>
  )
}
